import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsultation } from '../contexts/ConsultationContext';
import { useEbooks } from '../hooks/useEbooks';
import Maintenance from './Maintenance';
import '../styles/pages/ebook.css';
import ebook1Image from '../assets/images/ebook1.jpg';
import ebook2Image from '../assets/images/ebook2.jpg';
import ebook3Image from '../assets/images/ebook3.jpg';
import ebook4Image from '../assets/images/ebook4.jpg';
import ebook5Image from '../assets/images/ebook5.jpg';

interface EbookItem {
  id: string;
  title: string;
  imageUrl: string;
  driveLink: string;
}

const EBOOKS_STORAGE_KEY = 'userEbooks';

// Map tên file ảnh (BE lưu) -> ảnh bundle trong assets
const IMAGE_MAP: Record<string, string> = {
  'ebook1.jpg': ebook1Image,
  'ebook2.jpg': ebook2Image,
  'ebook3.jpg': ebook3Image,
  'ebook4.jpg': ebook4Image,
  'ebook5.jpg': ebook5Image,
};

function resolveImage(url?: string): string {
  if (!url) return ebook1Image;
  // URL ngoài hoặc ảnh upload (base64 data URI) → dùng trực tiếp
  if (/^(https?:\/\/|data:image\/)/.test(url)) return url;
  // Tên file ảnh bundle sẵn
  return IMAGE_MAP[url] || ebook1Image;
}

const Ebook: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { openPopup } = useConsultation();
  const isEn = i18n.language?.startsWith('en');
  const { data: ebookData, error: ebookError } = useEbooks();
  const [userEbooks, setUserEbooks] = useState<string[]>([]);
  const [hasConsultedBefore, setHasConsultedBefore] = useState(() => {
    return localStorage.getItem('hasConsultedBefore') === 'true';
  });

  // Map ebook từ Backend (qua cache version-gate + 24h) sang dạng hiển thị
  const ebooks: EbookItem[] = useMemo(() => {
    return (ebookData ?? []).map((e) => ({
      id: e.slug,
      // Chuyển "\n" (2 ký tự do dán từ JSON) thành xuống dòng thật để hiển thị đúng
      title: ((isEn ? e.titleEn : e.title) || e.title || '').replace(/\\n/g, '\n'),
      imageUrl: resolveImage(e.imageUrl),
      driveLink: (isEn ? e.driveLinkEn : e.driveLink) || e.driveLink || '#',
    }));
  }, [ebookData, isEn]);

  // Load user's ebooks from localStorage
  useEffect(() => {
    const savedEbooks = localStorage.getItem(EBOOKS_STORAGE_KEY);
    if (savedEbooks) {
      setUserEbooks(JSON.parse(savedEbooks));
    }
  }, []);

  // Listen for consultation completion
  useEffect(() => {
    const handleEbookUnlocked = (event: CustomEvent) => {
      const ebookId = event.detail.ebookId;
      if (ebookId && !userEbooks.includes(ebookId)) {
        const updatedEbooks = [...userEbooks, ebookId];
        setUserEbooks(updatedEbooks);
        localStorage.setItem(EBOOKS_STORAGE_KEY, JSON.stringify(updatedEbooks));
      }
    };

    const handleConsultationCompleted = () => {
      setHasConsultedBefore(true);
    };

    window.addEventListener('ebookUnlocked', handleEbookUnlocked as EventListener);
    window.addEventListener('consultationCompleted', handleConsultationCompleted);
    return () => {
      window.removeEventListener('ebookUnlocked', handleEbookUnlocked as EventListener);
      window.removeEventListener('consultationCompleted', handleConsultationCompleted);
    };
  }, [userEbooks]);

  const handleGetEbook = (ebookId: string, driveLink: string) => {
    if (hasConsultedBefore) {
      if (!userEbooks.includes(ebookId)) {
        const updatedEbooks = [...userEbooks, ebookId];
        setUserEbooks(updatedEbooks);
        localStorage.setItem(EBOOKS_STORAGE_KEY, JSON.stringify(updatedEbooks));
      }
      handleOpenEbook(driveLink);
    } else {
      sessionStorage.setItem('pendingEbookId', ebookId);
      openPopup('ebook');
    }
  };

  const handleOpenEbook = (driveLink: string) => {
    window.open(driveLink, '_blank');
  };

  const hasEbook = (ebookId: string) => userEbooks.includes(ebookId);

  // Không tải được ebook từ BE → bảo trì
  if (ebookError) {
    return <Maintenance />;
  }

  return (
    <div className="ebook-page">
      <div className="ebook-container">
        <div className="ebooks-section">
          <div className="ebooks-grid">
            {ebooks.map((ebook) => (
              <div key={ebook.id} className="ebook-card">
                <div className="ebook-badge">{t('ebook.freeTag')}</div>
                <div className="ebook-image">
                  <img src={ebook.imageUrl} alt={ebook.title} />
                </div>
                <div className="ebook-content">
                  <h3 style={{ whiteSpace: 'pre-line' }}>{ebook.title}</h3>
                  <button
                    className="ebook-button"
                    onClick={() =>
                      hasEbook(ebook.id) ? handleOpenEbook(ebook.driveLink) : handleGetEbook(ebook.id, ebook.driveLink)
                    }
                  >
                    {hasEbook(ebook.id) ? t('ebook.openButton') : t('ebook.getButton')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ebook;
